import { useEffect } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import type { FrontResponse } from '@/features/Setting/Integrations/integrationRedirection/front';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getFrontUrl } from '@/utils/integration';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { FrontInfo } from '../types';
import FrontInboxSettingModal from './FrontInboxSettingModal';
import { useFront } from './useFront';

export const FrontItem = (props: IntegrationItemProps) => {
  const { isConnector } = props;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    frontWorkspace,
    disconnectFrontMutation,
    requestImportMutation,
    updateInboxesMutation,
    updatePrivacyMutation,
    fetchInboxInfosMutation,
  } = useFront(!isConnector);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<FrontResponse | null>('front-redirect', null);

  const importModalState = useModalState<{
    workspace: FrontInfo;
    isNew: boolean;
  }>();
  const inboxSettingModalState = useModalState();

  // Handle OAuth redirect callback
  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    fetchFeedbackSources(currWorkspace.alias);

    // Open inbox setting modal after successful connection
    inboxSettingModalState.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirect, isConnector]);

  const handleConnectClick = () => {
    if (!currWorkspace) return;
    const url = getFrontUrl(currWorkspace.alias);
    window.open(url);
  };

  const handleDisconnect = () => {
    disconnectFrontMutation();
  };

  const handleImportWorkspace = async (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  if (isConnector && frontWorkspace) return null;

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!frontWorkspace}
        source={SOURCE_TYPE.FRONT}
        description="Import chats as feedback to Syncly."
        connectedDisplay={frontWorkspace?.name || 'Front workspace'}
        info={frontWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={handleConnectClick}
        additionalActions={(workspace) => (
          <div className="flex gap-2">
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace: workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
            <Button
              variant="outlined"
              onClick={() => inboxSettingModalState.open()}
            >
              Manage Channel
            </Button>
          </div>
        )}
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
      >
        {frontWorkspace && (
          <IntegrationImportModal
            isNew={importModalState.modalState?.isNew || false}
            title={'Import Front chats'}
            subTitle={'Front'}
            subTitleList={frontWorkspace.inbox_infos
              ?.map((info) => info.name)
              .join('/ ')}
            historyList={frontWorkspace.history || []}
            onSubmitImport={(start_date, end_date) =>
              handleImportWorkspace({ start_date, end_date })
            }
          />
        )}
      </Modal>

      <Modal
        status={inboxSettingModalState.status}
        closeModal={inboxSettingModalState.close}
      >
        {frontWorkspace && (
          <FrontInboxSettingModal
            workspace={frontWorkspace}
            onUpdateInboxes={updateInboxesMutation}
            onUpdatePrivacy={updatePrivacyMutation}
            onFetchInboxInfos={(_, callbacks) => {
              fetchInboxInfosMutation(undefined, {
                onSuccess: callbacks.onSuccess,
              });
            }}
          />
        )}
      </Modal>
    </>
  );
};
